<fui-heading [text]="'AI Assistant Config'" [level]="5"></fui-heading>
<span class="text-muted">Manage AI Assistant configurations for each client application and model.</span>

@if (loading) {
  <myp-inline-loader [loading]="loading" height="200px" spinnerName="ai-assistant-loader"></myp-inline-loader>
}

@if (!loading) {
  <div class="row m-t-20">
    <div class="col-12 m-b-20">
      <button class="brand-btn-reverse sm" (click)="addConfig()">
        <fa-icon icon="plus"></fa-icon>
        Add Config
      </button>
    </div>
    <div class="col-12">
      @if (!configs.length) {
        <p class="text-muted">No configurations found. Add one to get started.</p>
      }
      @for (config of configs; track config.client_id + "/" + config.model + "/" + config.request_type) {
        <div class="d-flex align-items-center justify-content-between p-y-10" style="border-bottom: 1px solid rgba(255, 255, 255, 0.1)">
          <div>
            <strong>{{ config.client_id }} / {{ config.model }} / {{ config.request_type }}</strong>
            @if (config.vector_store_id) {
              <span class="text-muted m-l-10">{{ config.vector_store_id }}</span>
            }
            @if (config.daily_message_limit) {
              <span class="text-muted m-l-10">({{ config.daily_message_limit }} {{ "admin_portal.msgs_per_day" | translate }})</span>
            }
            @if (config.daily_token_limit) {
              <span class="text-muted m-l-10">({{ config.daily_token_limit | number }} {{ "admin_portal.tokens_per_day" | translate }})</span>
            }
          </div>
          <div>
            <button class="small-btn m-r-5" (click)="editConfig(config)">
              <fa-icon icon="pencil"></fa-icon>
              Edit
            </button>
            <button class="small-btn m-r-5" (click)="duplicateConfig(config)">
              <fa-icon icon="copy"></fa-icon>
              Duplicate
            </button>
            <button class="small-btn" (click)="deleteConfig(config)">
              <fa-icon icon="times"></fa-icon>
              Delete
            </button>
          </div>
        </div>
      }
    </div>
  </div>
}

<ng-template #editConfigModal let-modal>
  <div class="modal-header">
    <h4 class="modal-title">{{ modalTitle }}</h4>
    <button ngbAutofocus class="close" aria-label="Close" (click)="modal.dismiss('Cross click')">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
  <div class="modal-body">
    <div class="row">
      <div class="form-group col-md-4">
        <label class="control-label">Client ID</label>
        <input class="form-control" [disabled]="!isNew" [(ngModel)]="editingConfig.client_id" placeholder="e.g. STUDIO_ONE" />
      </div>
      <div class="form-group col-md-4">
        <label class="control-label">Model</label>
        <input class="form-control" [disabled]="!isNew" [(ngModel)]="editingConfig.model" placeholder="e.g. gpt_4_nano" />
      </div>
      <div class="form-group col-md-4">
        <label class="control-label">Request Type</label>
        <select class="form-control" [disabled]="!isNew" [(ngModel)]="editingConfig.request_type">
          <option value="">— Select —</option>
          <option value="classification">classification</option>
          <option value="app">app</option>
          <option value="creative">creative</option>
          <option value="oos">oos</option>
          <option value="dev">dev</option>
        </select>
      </div>
      <div class="form-group col-md-6">
        <label class="control-label">Vector Store ID</label>
        <input class="form-control" [(ngModel)]="editingConfig.vector_store_id" placeholder="vs_abc123" />
      </div>
      <div class="form-group col-md-6">
        <label class="control-label">Max Output Tokens</label>
        <input type="number" class="form-control" [(ngModel)]="editingConfig.max_output_tokens" />
      </div>
      <div class="form-group col-12">
        <label class="control-label">Search Links (one per line or comma-separated)</label>
        <textarea class="form-control" rows="3" [value]="getSearchLinksString()" (input)="setSearchLinksFromString($any($event.target).value)"></textarea>
      </div>
      <div class="form-group col-12">
        <label class="control-label">Classification Prompt</label>
        <textarea class="form-control" rows="4" [(ngModel)]="editingConfig.classification_prompt"></textarea>
      </div>
      <div class="form-group col-12">
        <label class="control-label">App Prompt</label>
        <textarea class="form-control" rows="4" [(ngModel)]="editingConfig.app_prompt"></textarea>
      </div>
      <div class="form-group col-12">
        <label class="control-label">Creative Prompt</label>
        <textarea class="form-control" rows="4" [(ngModel)]="editingConfig.creative_prompt"></textarea>
      </div>
      <div class="form-group col-12">
        <label class="control-label">Out-of-Scope Prompt</label>
        <textarea class="form-control" rows="4" [(ngModel)]="editingConfig.oos_prompt"></textarea>
      </div>
      <div class="form-group col-12">
        <label class="control-label">Dev Prompt</label>
        <textarea class="form-control" rows="4" [(ngModel)]="editingConfig.dev_prompt"></textarea>
      </div>

      <div class="col-12 m-t-10">
        <h5 class="section-header">{{ "admin_portal.rate_limiting" | translate }}</h5>
      </div>
      <div class="form-group col-md-6">
        <label class="control-label">{{ "admin_portal.daily_message_limit" | translate }}</label>
        <input type="number" class="form-control" min="0" [(ngModel)]="editingConfig.daily_message_limit" [placeholder]="'admin_portal.daily_message_limit_placeholder' | translate" />
        <small class="text-muted">{{ "admin_portal.daily_message_limit_help" | translate }}</small>
      </div>
      <div class="form-group col-md-6">
        <label class="control-label">{{ "admin_portal.daily_token_limit" | translate }}</label>
        <input type="number" class="form-control" min="0" [(ngModel)]="editingConfig.daily_token_limit" [placeholder]="'admin_portal.daily_token_limit_placeholder' | translate" />
        <small class="text-muted">{{ "admin_portal.daily_token_limit_help" | translate }}</small>
      </div>

      <div class="col-12 m-t-10">
        <h5 class="section-header">Model Parameters</h5>
      </div>
      <div class="form-group col-md-6">
        <label class="control-label">Temperature (0–2)</label>
        <input type="number" class="form-control" min="0" max="2" step="0.1" [(ngModel)]="editingConfig.temperature" placeholder="e.g. 0.7" />
      </div>
      <div class="form-group col-md-6">
        <label class="control-label">Top P (0–1)</label>
        <input type="number" class="form-control" min="0" max="1" step="0.05" [(ngModel)]="editingConfig.top_p" placeholder="e.g. 1.0" />
      </div>
      <div class="form-group col-md-6">
        <label class="control-label">Reasoning</label>
        <select class="form-control" [(ngModel)]="editingConfig.reasoning">
          <option value="">— None —</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
      </div>
      <div class="form-group col-md-6">
        <label class="control-label">Verbosity</label>
        <select class="form-control" [(ngModel)]="editingConfig.verbosity">
          <option value="">— None —</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
      </div>
    </div>
  </div>
  <div class="modal-footer">
    <button class="btn btn-primary" [disabled]="saving" (click)="submit()">
      <fa-icon [icon]="saving ? 'spinner' : 'check'" [animation]="saving ? 'spin' : undefined"></fa-icon>
      {{ saving ? ("general.saving_dots" | translate) : ("general.save" | translate) }}
    </button>
  </div>
</ng-template>
